// src/screens/ReferenceScreen/index.tsx
import Navbar from 'components/navbar';
import { observer } from 'mobx-react';
import { TabPanel, TabView } from 'primereact/tabview';
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import BPMByPillCountTable from './components/BPMByPillCountTable';
import CleanTimeTable from './components/CleanTimeTable';
import ClearTimeTable from './components/ClearTimeTable';
import DrugReferenceByNDCTable from './components/DrugReferenceByNDCTable';
import DrugReferenceByConceptCodeTable from './components/DrugReferenceByConceptCodeTable';
import LVTimeTable from './components/LVTimeTable';
import RoomsTable from './components/RoomsTable';
import SetupTimeTable from './components/SetupTable';
import ShiftTimingsTable from './components/ShiftTimingsTable';
import classes from './referenceScreen.module.scss';
import DrugReferenceFgNdcToBulkNdcTable from './components/DrugReferenceFgNdcToBulkNdcTable';
import RoomBpmByRunTypeTable from './components/RoomBpmByRunType';
import { usePpsAccess } from 'hooks/usePpsAccess';
import BpmByNdcAndJobTypeTable from './components/BpmByNdcAndJobTypeTable';

const ReferenceScreen = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);
  const { canEditReferenceTables } = usePpsAccess();
  const tabViewRef = useRef<HTMLDivElement>(null);

  // Read the tab index from the URL hash on initial load
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const tabIndex = parseInt(hash.replace('#', ''), 10);
      if (!isNaN(tabIndex)) {
        setActiveIndex(tabIndex);
      }
    }
  }, [location.hash]);

  // Update the URL hash when the active tab changes
  const handleTabChange = (e: { index: number }) => {
    setActiveIndex(e.index);
    navigate(`#${e.index}`, { replace: true });
  };

  // Check scroll position and update button visibility
  const checkScrollPosition = () => {
    if (tabViewRef.current) {
      const tabNav = tabViewRef.current.querySelector('.p-tabview-nav') as HTMLElement;
      if (tabNav) {
        const { scrollLeft, scrollWidth, clientWidth } = tabNav;
        setShowLeftScroll(scrollLeft > 0);
        setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
      }
    }
  };

  // Handle scroll buttons
  const handleScroll = (direction: 'left' | 'right') => {
    if (tabViewRef.current) {
      const tabNav = tabViewRef.current.querySelector('.p-tabview-nav') as HTMLElement;
      if (tabNav) {
        const scrollAmount = 150; // Smaller scroll amount
        const newScrollLeft =
          tabNav.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
        tabNav.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  // Add scroll event listener and initial check
  useEffect(() => {
    const checkAndSetupScroll = () => {
      if (tabViewRef.current) {
        const tabNav = tabViewRef.current.querySelector('.p-tabview-nav') as HTMLElement;
        if (tabNav) {
          tabNav.addEventListener('scroll', checkScrollPosition);
          // Initial check
          checkScrollPosition();

          return () => {
            tabNav.removeEventListener('scroll', checkScrollPosition);
          };
        }
      }
    };

    // Wait for DOM to be ready
    const timeoutId = setTimeout(checkAndSetupScroll, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // Pass down the edit permission to all reference table components
  const canEdit = canEditReferenceTables();

  return (
    <div className={classes.referenceScreenContainer}>
      <Navbar heading="Reference" />
      <div className={classes.content}>
        <h1 className={classes.mainTitle}>Reference</h1>

        <div className={classes.tabViewContainer}>
          {showLeftScroll && (
            <Button
              icon="pi pi-chevron-left"
              className={classes.scrollButton}
              onClick={() => handleScroll('left')}
              style={{ backgroundColor: '#522d6e', borderColor: '#522d6e' }}
            />
          )}

          <div className={classes.tabViewWrapper} ref={tabViewRef}>
            <TabView
              activeIndex={activeIndex}
              onTabChange={handleTabChange}
              className={classes.tabView}
              style={{ fontSize: '0.8rem' }}
            >
              <TabPanel header="Shift Timings">
                <ShiftTimingsTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="Rooms">
                <RoomsTable />
              </TabPanel>
              <TabPanel header="Clean Time">
                <CleanTimeTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="Clear Time">
                <ClearTimeTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="Setup Time">
                <SetupTimeTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="LV Time">
                <LVTimeTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              {/* <TabPanel header="Recipe BPM">
                <RecipeBPMTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel> */}
              <TabPanel header="Room BPM By Run Type and Job Type">
                <RoomBpmByRunTypeTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="BPM by NDC and Job Types">
                <BpmByNdcAndJobTypeTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="BPM by Pill Count">
                <BPMByPillCountTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="Drug reference FG NDC to Bulk NDC Mapping">
                <DrugReferenceFgNdcToBulkNdcTable
                  showAddButton={canEdit}
                  showActionColumn={canEdit}
                />
              </TabPanel>
              <TabPanel header="Drug reference by NDC">
                <DrugReferenceByNDCTable showAddButton={canEdit} showActionColumn={canEdit} />
              </TabPanel>
              <TabPanel header="Drug reference by Concept Code">
                <DrugReferenceByConceptCodeTable
                  showAddButton={canEdit}
                  showActionColumn={canEdit}
                />
              </TabPanel>
            </TabView>
          </div>

          {showRightScroll && (
            <Button
              icon="pi pi-chevron-right"
              className={classes.scrollButton}
              onClick={() => handleScroll('right')}
              style={{ backgroundColor: '#522d6e', borderColor: '#522d6e' }}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default ReferenceScreen;
